import { useState } from 'react';
import {
  CaretDown,
  CaretUp,
  CheckCircle,
  WarningCircle,
  X,
  YoutubeLogo,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useDirectUploads } from '@/hooks/useYouTube';
import {
  type DirectUploadEntry,
  cancelDirectUpload,
  dismissDirectUpload,
  retryDirectUpload,
} from '@/utils/youtubeUploads';
import { cn } from '@/lib/utils';

function percentOf(entry: DirectUploadEntry): number {
  return entry.totalBytes
    ? Math.round((entry.sentBytes / entry.totalBytes) * 100)
    : 0;
}

function label(entry: DirectUploadEntry): string {
  switch (entry.phase) {
    case 'queued':
      return 'Waiting to upload';
    case 'uploading':
      return `Uploading ${percentOf(entry)}%`;
    case 'finishing':
      return 'Confirming with YouTube…';
    case 'done':
      return 'On YouTube';
    case 'paused':
      return entry.error ?? 'Paused';
    case 'cancelled':
      return 'Cancelled';
    default:
      return entry.error ?? 'Upload failed';
  }
}

/**
 * Studio's uploads pop-up: videos this page is sending straight to YouTube.
 *
 * Shown on every screen, because the uploads carry on while you move around
 * the app — only closing or reloading the page stops them, which the browser
 * asks about first. Renders nothing when there is nothing to show.
 */
export function DirectUploadsPanel() {
  const uploads = useDirectUploads();
  const [collapsed, setCollapsed] = useState(false);
  if (!uploads.length) return null;

  const active = uploads.filter(
    (entry) =>
      entry.phase === 'queued' ||
      entry.phase === 'uploading' ||
      entry.phase === 'finishing'
  );
  const heading = active.length
    ? `Uploading ${active.length} video${active.length === 1 ? '' : 's'} to YouTube`
    : 'Uploads to YouTube';

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 overflow-hidden rounded-lg border border-border bg-card shadow-lg">
      <div className="flex items-center justify-between gap-2 border-b border-border py-2.5 pl-4 pr-2.5">
        <div className="flex min-w-0 items-center gap-2 text-sm font-medium">
          <YoutubeLogo size={18} weight="fill" className="text-[#ff0000]" />
          <span className="truncate">{heading}</span>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          className="rounded p-1 text-muted-foreground hover:bg-muted"
          aria-label={collapsed ? 'Show uploads' : 'Hide uploads'}
        >
          {collapsed ? <CaretUp size={14} /> : <CaretDown size={14} />}
        </button>
      </div>
      {!collapsed && (
        <>
          {active.length > 0 && (
            <p className="bg-muted/50 px-4 py-2 text-[11px] leading-relaxed text-muted-foreground">
              Keep this page open until they finish — the files go from this
              browser straight to YouTube.
            </p>
          )}
          <ul className="max-h-72 divide-y divide-border overflow-y-auto">
            {uploads.map((entry) => {
              const running =
                entry.phase === 'queued' ||
                entry.phase === 'uploading' ||
                entry.phase === 'finishing';
              return (
                <li key={entry.uploadId} className="space-y-2 px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 space-y-0.5">
                      <div className="truncate text-xs font-medium">
                        {entry.fileName}
                      </div>
                      <div
                        className={cn(
                          'flex items-center gap-1 text-[11px]',
                          entry.phase === 'failed'
                            ? 'text-destructive'
                            : entry.phase === 'paused'
                              ? 'text-amber-700 dark:text-amber-300'
                              : 'text-muted-foreground'
                        )}
                      >
                        {entry.phase === 'done' && (
                          <CheckCircle
                            size={12}
                            weight="fill"
                            className="text-emerald-600"
                          />
                        )}
                        {(entry.phase === 'failed' ||
                          entry.phase === 'paused') && (
                          <WarningCircle size={12} weight="fill" />
                        )}
                        <span className="line-clamp-2">{label(entry)}</span>
                      </div>
                    </div>
                    {running ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[11px]"
                        onClick={() =>
                          void cancelDirectUpload(entry.uploadId).catch(
                            () => undefined
                          )
                        }
                      >
                        Cancel
                      </Button>
                    ) : entry.phase === 'paused' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-[11px]"
                        onClick={() => retryDirectUpload(entry.uploadId)}
                      >
                        Retry
                      </Button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => dismissDirectUpload(entry.uploadId)}
                        className="rounded p-0.5 text-muted-foreground hover:bg-muted"
                        aria-label="Dismiss"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                  {(entry.phase === 'uploading' ||
                    entry.phase === 'paused') && (
                    <Progress value={percentOf(entry)} />
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
